/* eslint-disable no-magic-numbers */

import { checkFileExtension } from '../../../report-builder/utils-generic';
import { FileUploadStatusProps } from 'carbon-react/lib/components/file-input';
import { toBase64 } from '../../../utils/file';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

interface UseExcelForecastUploadFieldOptions {
  acceptedFileExtensions?: string;
  maxFileSizeMB?: number;
  onChange?: (file: File) => void;
}

const DEFAULT_OPTIONS: Required<UseExcelForecastUploadFieldOptions> = {
  acceptedFileExtensions: '.xls,.xlsx',
  maxFileSizeMB: 25,
  onChange: () => void 0
};

export const useExcelForecastUploadField = (inputOptions?: UseExcelForecastUploadFieldOptions) => {
  const intl = useIntl();
  const { acceptedFileExtensions, maxFileSizeMB, onChange: onChangeOpt } = { ...DEFAULT_OPTIONS, ...inputOptions };

  const [file, setFile] = useState<File>();
  const [error, setError] = useState('');
  const [uploadStatus, setUploadStatus] = useState<FileUploadStatusProps>();

  const validateFile = useCallback(
    (file: File) => {
      if (!checkFileExtension(file.name, acceptedFileExtensions)) {
        setError(intl.formatMessage({ id: 'predict.file_not_supported', defaultMessage: 'File not supported' }));
        return false;
      } else if (file.size > maxFileSizeMB * 1024 * 1024) {
        setError(intl.formatMessage({ id: 'predict.file_too_big', defaultMessage: 'File is too big' }));
        return false;
      } else {
        setError('');
        return true;
      }
    },
    [acceptedFileExtensions, intl, maxFileSizeMB]
  );

  const reset = useCallback(() => {
    setFile(undefined);
    setError('');
    setUploadStatus(undefined);
  }, []);

  const onChange = useCallback(
    (files: FileList) => {
      if (validateFile(files[0])) {
        setFile(files[0]);
        onChangeOpt(files[0]);
      }
    },
    [onChangeOpt, validateFile]
  );

  // When File changes, read file contents and then update the uploadStatus data
  useEffect(() => {
    const updateStatus = async () => {
      if (file) {
        try {
          const href = await toBase64(file);
          setUploadStatus({
            filename: file.name,
            onAction: () => {
              setUploadStatus(undefined);
            },
            status: 'completed',
            href,
            iconType: 'file_excel',
            message: intl.formatMessage({
              id: 'common.file_loaded',
              defaultMessage: 'File loaded'
            })
          });
        } catch (e) {
          setUploadStatus(undefined);
        }
      } else {
        setUploadStatus(undefined);
      }
    };
    updateStatus();
  }, [file, intl]);

  return {
    error,
    file,
    onChange,
    reset,
    uploadStatus
  };
};
